// Tiny promise wrapper over IndexedDB. Falls back to an in-memory map if
// IndexedDB is unavailable (some locked-down kiosk browsers), in which case
// the sync server is the only durable copy.

import type { SyncRecord } from '../domain/sync.ts';

export interface StoredRecord extends SyncRecord {
  key: string;
  dirty?: boolean;
}

export interface LocalDB {
  loadAll(): Promise<StoredRecord[]>;
  putMany(records: StoredRecord[]): Promise<void>;
  getMeta<T>(key: string): Promise<T | undefined>;
  setMeta(key: string, value: unknown): Promise<void>;
  clear(): Promise<void>;
  readonly persistent: boolean;
}

const DB_NAME = 'arlo-hq';
const VERSION = 1;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

class IndexedLocalDB implements LocalDB {
  readonly persistent = true;
  private db: IDBDatabase;
  constructor(db: IDBDatabase) {
    this.db = db;
  }

  async loadAll(): Promise<StoredRecord[]> {
    const tx = this.db.transaction('records', 'readonly');
    return req(tx.objectStore('records').getAll()) as Promise<StoredRecord[]>;
  }

  async putMany(records: StoredRecord[]): Promise<void> {
    if (!records.length) return;
    const tx = this.db.transaction('records', 'readwrite');
    const os = tx.objectStore('records');
    for (const r of records) os.put(r);
    await done(tx);
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    const tx = this.db.transaction('meta', 'readonly');
    const row = (await req(tx.objectStore('meta').get(key))) as { key: string; value: T } | undefined;
    return row?.value;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    const tx = this.db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put({ key, value });
    await done(tx);
  }

  async clear(): Promise<void> {
    const tx = this.db.transaction(['records', 'meta'], 'readwrite');
    tx.objectStore('records').clear();
    tx.objectStore('meta').clear();
    await done(tx);
  }
}

class MemoryLocalDB implements LocalDB {
  readonly persistent = false;
  private records = new Map<string, StoredRecord>();
  private meta = new Map<string, unknown>();
  async loadAll() {
    return [...this.records.values()];
  }
  async putMany(records: StoredRecord[]) {
    for (const r of records) this.records.set(r.key, r);
  }
  async getMeta<T>(key: string) {
    return this.meta.get(key) as T | undefined;
  }
  async setMeta(key: string, value: unknown) {
    this.meta.set(key, value);
  }
  async clear() {
    this.records.clear();
    this.meta.clear();
  }
}

export async function openLocalDB(factory: IDBFactory | undefined = globalThis.indexedDB): Promise<LocalDB> {
  if (!factory) return new MemoryLocalDB();
  try {
    const open = factory.open(DB_NAME, VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains('records')) db.createObjectStore('records', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });
    };
    const db = await req(open);
    // Ask the browser not to evict our data under storage pressure.
    navigator.storage?.persist?.().catch(() => undefined);
    return new IndexedLocalDB(db);
  } catch (err) {
    console.warn('IndexedDB unavailable, using memory only', err);
    return new MemoryLocalDB();
  }
}

export function memoryDB(): LocalDB {
  return new MemoryLocalDB();
}
